import { useState, useEffect, useRef, useCallback, RefObject } from 'react';

import { PANEL_TIMINGS } from './timings';

interface UseDelayedHoverOptions {
  /** Delay before opening on hover (ms). Defaults to `PANEL_TIMINGS.openDelay` */
  delayDuration?: number,
  /** If a panel closed within this window, open immediately (ms). Defaults to `PANEL_TIMINGS.skipDelay` */
  skipDelayDuration?: number,
  /** Delay before closing on mouse leave (ms). Defaults to `PANEL_TIMINGS.leaveDelay` */
  leaveDelay?: number,
  /** Whether hover interaction is enabled. Default: true */
  enabled?: boolean,
}

// Shared across all instances — tracks when the last panel was closed
// so that switching between triggers opens immediately (skip-delay pattern)
let lastCloseTimestamp = 0;

export const getLastCloseTimestamp = (): number => lastCloseTimestamp;
export const setLastCloseTimestamp = (ts: number): void => { lastCloseTimestamp = ts; };

/**
 * A hover state hook inspired by Radix UI's NavigationMenu delay logic.
 *
 * - `delayDuration`: How long to wait before opening (prevents accidental triggers)
 * - `skipDelayDuration`: If a panel was recently closed, skip the open delay for fast switching
 * - `leaveDelay`: Grace period before closing after mouse leaves
 */
const useDelayedHover = (
  ref: RefObject<HTMLElement | null>,
  options: UseDelayedHoverOptions = {},
): boolean => {
  const {
    delayDuration = PANEL_TIMINGS.openDelay,
    skipDelayDuration = PANEL_TIMINGS.skipDelay,
    leaveDelay = PANEL_TIMINGS.leaveDelay,
    enabled = true,
  } = options;

  const [isHovered, setIsHovered] = useState(false);
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const open = useCallback(() => {
    clearTimers();
    setIsHovered(true);
  }, [clearTimers]);

  const close = useCallback(() => {
    clearTimers();
    setIsHovered(false);
    lastCloseTimestamp = Date.now();
  }, [clearTimers]);

  const handleEnter = useCallback((): void => {
    // Cancel any pending close
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    // If a panel was recently closed, skip the open delay (fast switching)
    const timeSinceLastClose = Date.now() - lastCloseTimestamp;
    if (timeSinceLastClose < skipDelayDuration) {
      open();
      return;
    }

    // Otherwise, wait for delayDuration before opening
    openTimerRef.current = setTimeout(() => {
      open();
      openTimerRef.current = null;
    }, delayDuration);
  }, [delayDuration, skipDelayDuration, open]);

  const handleLeave = useCallback((): void => {
    // Cancel any pending open
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }

    // Start close timer
    closeTimerRef.current = setTimeout(() => {
      close();
      closeTimerRef.current = null;
    }, leaveDelay);
  }, [leaveDelay, close]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      setIsHovered(false);
      return undefined;
    }

    const el = ref.current;
    if (!el) return undefined;

    el.addEventListener('mouseenter', handleEnter);
    el.addEventListener('mouseleave', handleLeave);

    return () => {
      el.removeEventListener('mouseenter', handleEnter);
      el.removeEventListener('mouseleave', handleLeave);
      clearTimers();
    };
  }, [ref, enabled, handleEnter, handleLeave, clearTimers]);

  return isHovered;
};

export default useDelayedHover;
